import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";

const POPUP_DELAY_MS = 1500;
const DISMISS_KEY = "freePassPopupDismissed";

export default function FreePassPopup() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY)) return;
    const timer = setTimeout(() => setVisible(true), POPUP_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem(DISMISS_KEY, "1");
  };

  return (
    <AnimatePresence>
      {visible && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={dismiss}
          />

          {/* Modal — bottom-sheet on mobile, centered on desktop */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 sm:inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 pointer-events-none"
          >
            <div className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl pointer-events-auto overflow-hidden">
              {/* Pink accent bar */}
              <div className="h-1.5 bg-gradient-to-r from-brand-pink to-brand-rose" />

              {/* Close button */}
              <button
                onClick={dismiss}
                className="absolute top-3 right-3 p-2 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="px-5 pt-6 pb-6 sm:px-6 sm:pt-8 text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-5 rounded-full bg-brand-pink/10 flex items-center justify-center">
                  <Gift className="w-7 h-7 sm:w-8 sm:h-8 text-brand-pink" />
                </div>

                <h2 className="text-2xl sm:text-3xl font-heading font-black text-gray-900 mb-2 uppercase">
                  Try Us Free
                </h2>
                <p className="text-sm sm:text-base text-gray-600 mb-5 sm:mb-6">
                  Experience everything we have to offer with a <strong>free 1-day guest pass</strong>. No commitment, no pressure — just a great workout.
                </p>

                <div className="flex flex-col gap-3">
                  <Link to="/free-pass" onClick={dismiss}>
                    <Button className="w-full bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider text-base py-3">
                      Get Your Free Pass
                    </Button>
                  </Link>
                  <button
                    onClick={dismiss}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors py-1"
                  >
                    No thanks, maybe later
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
